import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import logoLeaf from "@/assets/logo-leaf.svg";
import { useMagnetic } from "@/hooks/useMagnetic";
import { ease, duration } from "@/lib/motion";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Examples", href: "#showcase" },
  { label: "Process", href: "#process" },
  { label: "FAQ", href: "#faq" },
];

const getScrollTarget = (href: string) => {
  const key = href.replace(/^#/, "");
  return (
    document.querySelector<HTMLElement>(`[data-scroll-target="${key}"]`) ??
    document.querySelector<HTMLElement>(href)
  );
};

const scrollToSection = (href: string) => {
  const target = getScrollTarget(href);
  if (!target) return;
  const navHeight =
    parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--navbar-height"),
    ) || 76;
  const top = target.getBoundingClientRect().top + window.scrollY - navHeight;
  window.scrollTo({ top, behavior: "smooth" });
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  useMagnetic(ctaRef);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useLayoutEffect(() => {
    const updateNavbarHeight = () => {
      if (!barRef.current) return;
      document.documentElement.style.setProperty("--navbar-height", `${barRef.current.offsetHeight}px`);
    };

    updateNavbarHeight();
    window.addEventListener("resize", updateNavbarHeight);

    if (typeof ResizeObserver === "undefined" || !barRef.current) {
      return () => window.removeEventListener("resize", updateNavbarHeight);
    }

    const resizeObserver = new ResizeObserver(updateNavbarHeight);
    resizeObserver.observe(barRef.current);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateNavbarHeight);
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Focus trap for mobile menu
  useEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    if (!menu) return;
    const focusables = Array.from(menu.querySelectorAll<HTMLElement>("a, button"));
    focusables[0]?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  return (
    <header>
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: duration.deliberate, ease: ease.smooth }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow] duration-500 ease-out ${
        scrolled
          ? "bg-charcoal shadow-[0_1px_0_0_hsl(var(--cream)/0.08)]"
          : "bg-transparent"
      }`}
    >
      {/* Subtle bottom fade for blending into content */}
      <div
        className={`absolute inset-x-0 bottom-0 h-px transition-opacity duration-500 ${
          scrolled ? "opacity-100" : "opacity-0"
        }`}
        style={{
          background: "linear-gradient(90deg, transparent, hsl(var(--cream) / 0.08) 30%, hsl(var(--cream) / 0.08) 70%, transparent)",
        }}
      />

      <div
        ref={barRef}
        className="container mx-auto flex min-h-[4.25rem] items-center justify-between px-4 py-2 sm:px-6 md:min-h-[4.75rem] md:px-8 md:py-2.5"
      >
        {/* Logo */}
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="group flex min-w-0 items-center gap-2 py-1.5 -my-1.5 sm:gap-2.5"
        >
          <img
            src={logoLeaf}
            alt=""
            className={`h-8 w-8 shrink-0 transition-transform duration-500 group-hover:scale-110 sm:h-9 sm:w-9`}
          />
          <span className="min-w-0 whitespace-nowrap font-heading text-[1.1rem] leading-none tracking-tight sm:text-xl">
            <span className="text-gradient-green text-2xl">Greenleaf</span>
            <span className="text-2xl text-cream transition-opacity duration-500">
              {" "}Digital
            </span>
          </span>
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(link.href);
              }}
              className={`relative text-sm font-medium transition-colors duration-300 after:absolute after:bottom-[-4px] after:left-0 after:h-0.5 after:w-full after:origin-bottom-right after:scale-x-0 after:bg-sage after:transition-transform after:duration-300 after:content-[''] hover:after:origin-bottom-left hover:after:scale-x-100 text-cream/60 hover:text-cream`}
            >
              {link.label}
            </a>
          ))}
          <a
            href="tel:8287472665"
            className="hidden lg:inline text-sm font-medium text-cream/60 hover:text-cream transition-colors duration-300"
          >
            (828) 747-2665
          </a>
          <a
            ref={ctaRef}
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("#contact");
            }}
            className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 transition-[background-color] duration-300 hover:bg-forest-light active:scale-[0.98] will-change-transform"
          >
            Book a Free Call
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          ref={buttonRef}
          aria-label={open ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={open}
          aria-controls="mobile-menu"
          className="shrink-0 p-2.5 -mr-2.5 text-cream md:hidden transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/50 rounded-lg"
          onClick={() => setOpen(!open)}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: duration.smooth, ease: ease.smooth }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 top-full bg-charcoal/60 backdrop-blur-sm md:hidden"
              aria-hidden="true"
            />
          <motion.div
            ref={menuRef}
            id="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: duration.smooth, ease: ease.smooth }}
            className="absolute left-0 right-0 top-full border-t border-cream/5 bg-charcoal px-6 pt-4 pb-6 space-y-4 shadow-xl shadow-black/40 md:hidden"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  scrollToSection(link.href);
                }}
                className="block text-base font-medium text-cream/80 py-3 transition-colors hover:text-cream"
              >
                {link.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                scrollToSection("#contact");
              }}
              className="block rounded-lg bg-primary px-5 py-3.5 text-center text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20"
            >
              Book a Free Call
            </a>
            <a
              href="tel:8287472665"
              onClick={() => setOpen(false)}
              className="block text-center text-base font-medium text-cream/70 py-2 hover:text-cream transition-colors"
            >
              (828) 747-2665
            </a>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.nav>
    </header>
  );
};

export default Navbar;
